import React, { useState, useEffect } from 'react';
import { 
  Typography, Container, Paper, Grid, Box, Tabs, Tab, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Avatar, Button, Chip,
  Card, CardContent, Tooltip, Fade, Grow
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, Cancel, HourglassEmpty, Group, DateRange,
  KeyboardArrowRight, BusinessCenter, Stars, PersonAdd, EventAvailable, CheckCircleOutline
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getAllAttendance, updateAttendanceStatus } from '../../api/attendanceAPI';
import { getAllLeaves, updateLeaveStatus } from '../../api/leaveAPI';
import { getAllHolidays } from '../../api/holidayAPI';
import { getAllUsers } from '../../api/userAPI';
import moment from 'moment';

const HRManagement = () => {
  const { user } = useSelector((state) => state.tokens);
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [uRes, aRes, lRes, hRes] = await Promise.all([
        getAllUsers(),
        getAllAttendance({ range: 'today' }),
        getAllLeaves(),
        getAllHolidays()
      ]);

      if(uRes.data) {
        const activeOfficeEmp = uRes.data.filter(u => u.isOfficeEmp && u.isActive !== false);
        setUsers(activeOfficeEmp);

        const activeIds = activeOfficeEmp.map(u => u.id || u._id);
        if(aRes.data && aRes.data.success) {
          setAttendance(aRes.data.log.filter(a => activeIds.includes(a.user?._id)));
        }
        if(lRes.data && lRes.data.success) {
          setLeaves(lRes.data.leaves.filter(l => activeIds.includes(l.user?._id)));
        }
      } else {
        if(aRes.data && aRes.data.success) setAttendance(aRes.data.log);
        if(lRes.data && lRes.data.success) setLeaves(lRes.data.leaves);
      }
      if(hRes.data && hRes.data.success) setHolidays(hRes.data.holidays);
    } catch(err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateAttendance = async (id, status, halfDayStatus) => {
    try {
      await updateAttendanceStatus(id, { status, halfDayStatus });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleLeaveAction = async (id, status, reason = '') => {
    try {
      await updateLeaveStatus(id, { status, approvedBy: user._id, rejectionReason: reason });
      fetchData();
    } catch(err) { console.error(err); }
  };

  const getDayStatus = (userId) => {
    const todayStr = moment().format('YYYY-MM-DD');
    const att = attendance.find(a => a.user?._id === userId);
    if(att) return { label: 'Present', color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> };
    
    const onLeave = leaves.find(l => 
      l.user?._id === userId && 
      l.status === 'Approved' && 
      l.dates?.some(d => moment(d).format('YYYY-MM-DD') === todayStr)
    );
    if(onLeave) return { label: 'On Leave', color: 'info', icon: <DateRange sx={{ fontSize: 16 }} /> };
    
    return { label: 'Absent', color: 'error', icon: <Cancel sx={{ fontSize: 16 }} /> };
  };

  const pendingLeaves = leaves.filter(l => l.status === 'Pending');
  const upcomingHolidays = holidays
    .filter(h => moment(h.date).isSameOrAfter(moment(), 'day'))
    .sort((a,b) => moment(a.date) - moment(b.date))
    .slice(0, 5);

  const stats = {
    total: users.length,
    present: attendance.length,
    onLeave: users.filter(u => getDayStatus(u.id || u._id).label === 'On Leave').length,
    pending: pendingLeaves.length
  };

  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
      pt: 4, pb: 8, px: { xs: 2, md: 4 }
    }}>
      <Container maxWidth="xl">
        <Fade in={true} timeout={800}>
          <Box sx={{ mb: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 48, height: 48, borderRadius: '12px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(118, 75, 162, 0.3)'
              }}>
                <Stars sx={{ color: '#fff' }} />
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily: '"Inter", "Outfit", sans-serif'
              }}>
                HR Excellence Hub
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<CheckCircleOutline />}
                onClick={() => setActiveTab(1)}
                sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 600, px: 3, boxShadow: '0 4px 14px 0 rgba(102, 126, 234, 0.15)', borderColor: 'rgba(102, 126, 234, 0.5)' }}
              >
                Review Leaves
              </Button>
              <Button 
                variant="contained" 
                startIcon={<EventAvailable />}
                onClick={() => navigate('/holidays')}
                sx={{ 
                  borderRadius: 8, textTransform: 'none', fontWeight: 600, px: 3,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  boxShadow: '0 4px 14px 0 rgba(17, 153, 142, 0.39)',
                  '&:hover': { background: 'linear-gradient(135deg, #0f877d 0%, #32d970 100%)' }
                }}
              >
                Add Holiday
              </Button>
              <Button 
                variant="contained" 
                startIcon={<PersonAdd />}
                onClick={() => navigate('/users')}
                sx={{ 
                  borderRadius: 8, textTransform: 'none', fontWeight: 600, px: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 14px 0 rgba(118, 75, 162, 0.39)',
                  '&:hover': { background: 'linear-gradient(135deg, #5a6fd1 0%, #6a4392 100%)' }
                }}
              >
                Add Employee
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* KPI Cards */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            { label: 'Total Workforce', value: stats.total, icon: <Group />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#764ba2' },
            { label: 'Active Today', value: stats.present, icon: <CheckCircle />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#11998e' },
            { label: 'On Leave', value: stats.onLeave, icon: <DateRange />, gradient: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)', color: '#2193b0' },
            { label: 'Action Required', value: stats.pending, icon: <HourglassEmpty />, gradient: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)', color: '#f2994a' },
          ].map((kpi, idx) => (
            <Grow in={true} timeout={500 + (idx * 200)} key={idx}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 4, 
                  ...glassmorphismStyle,
                  position: 'relative', 
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
                  }
                }}>
                  <Box sx={{ 
                    position: 'absolute', top: -15, right: -15, 
                    width: 100, height: 100, borderRadius: '50%',
                    background: kpi.gradient, opacity: 0.1,
                    zIndex: 0
                  }} />
                  <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {kpi.label}
                      </Typography>
                      <Box sx={{ 
                        p: 1, borderRadius: '10px', 
                        background: `${kpi.color}15`, color: kpi.color,
                        display: 'flex'
                      }}>
                        {React.cloneElement(kpi.icon, { fontSize: 'small' })}
                      </Box>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#2d3748', fontFamily: '"Outfit", sans-serif' }}>
                      {kpi.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grow>
          ))}
        </Grid>

        <Paper sx={{ 
          borderRadius: 4, 
          ...glassmorphismStyle,
          overflow: 'hidden'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, val) => setActiveTab(val)} 
            sx={{ 
              px: 3, pt: 2, 
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(90deg, #667eea, #764ba2)'
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                minWidth: 120,
                color: '#718096',
                '&.Mui-selected': { color: '#4a5568' }
              }
            }}
          >
            <Tab label="Team Attendance" />
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Leave Requests
                {pendingLeaves.length > 0 && (
                  <Chip size="small" label={pendingLeaves.length} sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#ffe0e0', color: '#d32f2f', fontWeight: 'bold' }} />
                )}
              </Box>
            } />
            <Tab label="Holiday Calendar" />
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* TEAM OVERVIEW TAB */}
            <Fade in={activeTab === 0} timeout={400}>
              <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
                <TableContainer>
                  <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { borderBottom: '2px solid #edf2f7', color: '#a0aec0', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 2 } }}>
                        <TableCell>Employee Details</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Time Log</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((emp) => {
                        const log = attendance.find(a => a.user?._id === (emp.id || emp._id));
                        return (
                          <TableRow key={emp.id || emp._id} hover sx={{ 
                            '& td': { borderBottom: '1px solid #edf2f7', py: 2 },
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                            transition: 'background-color 0.2s'
                          }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ 
                                  bgcolor: 'transparent', color: '#fff', 
                                  background: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
                                  width: 42, height: 42, fontWeight: 'bold'
                                }}>
                                  {emp.fullName?.[0]}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#2d3748' }}>{emp.fullName}</Typography>
                                  <Typography variant="caption" sx={{ color: '#718096' }}>{emp.email}</Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                <Chip 
                                  label={log?.status === 'Half-day' ? `Half-day (${log.halfDayStatus})` : (log?.status || 'Absent')} 
                                  sx={{ 
                                    fontWeight: 600, borderRadius: '8px',
                                    bgcolor: log?.status === 'Present' ? '#e6fffa' : (log?.status === 'Half-day' ? '#fffaf0' : '#fff5f5'),
                                    color: log?.status === 'Present' ? '#319795' : (log?.status === 'Half-day' ? '#dd6b20' : '#e53e3e'),
                                  }}
                                  size="small" 
                                />
                                {log?.halfDayStatus === 'Pending' && log?.earlyCheckoutReason && (
                                  <Typography variant="caption" sx={{ color: '#718096', fontStyle: 'italic', maxWidth: 150, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {log.earlyCheckoutReason}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                                  <span style={{ color: '#a0aec0', fontSize: '0.8rem', marginRight: 4 }}>IN:</span>
                                  {log?.checkIn ? moment(log.checkIn).format('hh:mm A') : '--:--'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#4a5568', fontWeight: 500 }}>
                                  <span style={{ color: '#a0aec0', fontSize: '0.8rem', marginRight: 4 }}>OUT:</span>
                                  {log?.checkOut ? moment(log.checkOut).format('hh:mm A') : '--:--'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {log && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {log.status === 'Half-day' ? (
                                    <Tooltip title="Revert to Full Day">
                                      <Button size="small" variant="outlined" sx={{ 
                                          borderColor: '#38b2ac', color: '#319795', borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                                          '&:hover': { bgcolor: '#e6fffa', borderColor: '#319795' }
                                        }}
                                        onClick={() => handleUpdateAttendance(log._id, 'Present', 'Rejected')}
                                      >
                                        Mark Full Day
                                      </Button>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Mark as Half-day">
                                      <Button size="small" variant="outlined" sx={{ 
                                          borderColor: '#fbd38d', color: '#dd6b20', borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                                          '&:hover': { bgcolor: '#fffaf0', borderColor: '#dd6b20' }
                                        }}
                                        onClick={() => handleUpdateAttendance(log._id, 'Half-day', 'Approved')}
                                      >
                                        Mark Half-day
                                      </Button>
                                    </Tooltip>
                                  )}

                                  {log.halfDayStatus === 'Pending' && (
                                    <Button size="small" variant="contained" sx={{ 
                                        bgcolor: '#ed8936', borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none',
                                        '&:hover': { bgcolor: '#dd6b20', boxShadow: '0 4px 12px rgba(237, 137, 54, 0.3)' }
                                      }}
                                      onClick={() => handleUpdateAttendance(log._id, 'Half-day', 'Approved')}
                                    >
                                      Approve 1/2
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Fade>

            {/* LEAVE REQUESTS TAB */}
            <Fade in={activeTab === 1} timeout={400}>
              <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ '& th': { borderBottom: '2px solid #edf2f7', color: '#a0aec0', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 2 } }}>
                        <TableCell>Employee</TableCell>
                        <TableCell>Leave Details</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="right">Decision</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingLeaves.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, opacity: 0.5 }}>
                              <CheckCircle sx={{ fontSize: 60, color: '#a0aec0' }} />
                              <Typography sx={{ color: '#4a5568', fontWeight: 500 }}>All caught up! No pending requests.</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : pendingLeaves.map((req) => (
                        <TableRow key={req._id} hover sx={{ '& td': { borderBottom: '1px solid #edf2f7', py: 2 }, transition: 'background-color 0.2s' }}>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#2d3748' }}>{req.user?.fullName}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                              <Chip label={req.type === 'Paid' || req.type === 'Earned' ? 'Planned' : req.type} size="small" sx={{ bgcolor: '#edf2f7', color: '#4a5568', fontWeight: 600, borderRadius: '6px' }} />
                              <Typography variant="caption" sx={{ color: '#718096', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DateRange sx={{ fontSize: 14 }} />
                                {req.dates?.map(d => moment(d).format('DD MMM')).join(', ') || moment(req.startDate).format('DD MMM')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#4a5568', maxWidth: 300, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {req.reason}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button size="small" variant="contained" sx={{ 
                                  bgcolor: '#38a169', borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none',
                                  '&:hover': { bgcolor: '#2f855a', boxShadow: '0 4px 12px rgba(56, 161, 105, 0.3)' }
                                }} onClick={() => handleLeaveAction(req._id, 'Approved')}
                              >
                                Approve
                              </Button>
                              <Button size="small" variant="contained" sx={{ 
                                  bgcolor: '#e53e3e', borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none',
                                  '&:hover': { bgcolor: '#c53030', boxShadow: '0 4px 12px rgba(229, 62, 62, 0.3)' }
                                }} onClick={() => handleLeaveAction(req._id, 'Rejected')}
                              >
                                Decline
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Fade>

            {/* HOLIDAY CALENDAR TAB */}
            <Fade in={activeTab === 2} timeout={400}>
              <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
                <Grid container spacing={3}>
                  {upcomingHolidays.map((hol, idx) => (
                    <Grid item xs={12} md={6} key={hol._id}>
                      <Box sx={{ 
                        p: 3, borderRadius: '16px', bgcolor: '#fff',
                        border: '1px solid rgba(0,0,0,0.05)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                        display: 'flex', gap: 3, alignItems: 'center',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.05)' }
                      }}>
                        <Box sx={{ 
                          width: 70, height: 70, borderRadius: '12px', 
                          background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', boxShadow: '0 4px 15px rgba(253, 160, 133, 0.4)'
                        }}>
                          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>{moment(hol.date).format('DD')}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{moment(hol.date).format('MMM')}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5 }}>{hol.name}</Typography>
                          <Typography variant="body2" sx={{ color: '#718096' }}>{hol.description || 'Public Holiday'}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                  {upcomingHolidays.length === 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, opacity: 0.5, py: 8 }}>
                        <BusinessCenter sx={{ fontSize: 60, color: '#a0aec0' }} />
                        <Typography sx={{ color: '#4a5568', fontWeight: 500 }}>No upcoming holidays scheduled.</Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Fade>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default HRManagement;
